import { Button, Stack } from '@mui/material';
import { CreatePayOrderDto,PayOrderDto, PrepaymentDto, UpdatePayOrderDto } from '@sr/dto';
import { FormDialog } from 'shared/ui/form-dialog';
import { PayOrderFormFields } from 'components/PayOrders/pay-order.form-fields';
import { notificationsConfig, PayOrdersList } from 'components/PayOrders/pay-orders-list';
import { FormData, formTools } from 'components/PayOrders/pay-order.form-tools';
import useConfirmDialog from 'hooks/confirm-dialog.hook';
import { useCreateOrEditDialog } from 'hooks/create-or-edit-dialog.hook';
import { usePayOrders } from 'hooks/payorders.hook';
import { useSnack } from 'shared/ui/snack';
import { useSearchParams } from 'react-router-dom';
import { fetchPayOrder } from 'shared/api/payorders';
import { useTitleHook } from 'hooks/page-title.hook';
import { Heading } from 'shared/ui/list-page-heading';
import { usePagingParams } from 'shared/hooks/paging-params';
import { useMemo, useRef, useState } from 'react';
import { ButtonLink } from 'shared/ui/link-base';
import AddIcon from '@mui/icons-material/Add';
import { useDeletePrepayment } from '../../entities/salary/prepayment/prepayment.hook';
import { useActionCreators } from '../../shared/store';
import { prepaymentDialogActions } from '../../entities/salary/prepayment/prepayment-dialog/prepayment-dialog.slice';
import { fetchPrepayment } from '../../entities/salary/prepayment/prepayment.api';
import { PrepaymentDialogForm } from '../../entities/salary/prepayment/prepayment-dialog/prepayment-dialog.form';
import { payOrdersApi } from 'entities/pay-orders/pay-orders.api';
import { extractNestJsErrorMessage } from 'shared/api/rtk-query';
import { transferFormTools, TransferFormData } from 'components/PayOrders/pay-order-transfer.form';
import { PayOrderTransferFormFields } from 'components/PayOrders/pay-order-transfer.form-fields';
import { Delete } from '@mui/icons-material';

function isSalaryEntry(x: PayOrderDto): x is (PayOrderDto & {rows: [{salaryEntryId: number}]}) {
  return !!(x.rows && x.rows[0]?.salaryEntryId);
}

const splitPayordersPrepayments = (payorders: PayOrderDto[], selectedIds: number[]) => {
  const payordersIds: number[] = [];
  const salaryEntryIds: number[] = [];

  payorders.forEach((x) => {
    if (!(selectedIds.includes(x.id))) return;
    if (isSalaryEntry(x)) {
      salaryEntryIds.push(x.rows[0].salaryEntryId);
    } else payordersIds.push(x.id);
  });

  return {
    payordersIds,
    salaryEntryIds
  };
};

const PayOrdersListPage = () => {
  useTitleHook('Бухгалтерия');
  const { showSuccessDelete, showFailedDelete } = useSnack(notificationsConfig);
  const showConfirm = useConfirmDialog();
  const { showSuccess, showError } = useSnack();

  const [paging, setPaging] = usePagingParams();
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const [ selectedPayOrderIds, setSelectedPayOrderIds ] = useState<number[]>([]);
  const transferInitialValues = useRef(transferFormTools.emptyValues());

  const getPayOrdersSearchParams = useMemo(() => {
    const relatedFields = ['managerId', 'statusId', 'bankAccountId', 'groupIds', 'toDate', 'fromDate', 'toPrice', 'fromPrice'];
    const newParams = new URLSearchParams();

    [...searchParams.entries()].forEach(([key, value]) => {
      if (relatedFields.includes(key)) {
        newParams.append(key, value);
      }
    });
    return newParams;
  }, [searchParams]);

  const collection = usePayOrders(paging, getPayOrdersSearchParams);

  const prepaymentToEdit = useRef<PrepaymentDto | null>(null);
  const { openDialog: openPrepaymentDialog, closeDialog: closePrepaymentDialog } = useActionCreators(prepaymentDialogActions);

  const { refetch: refetchPayOrders } = payOrdersApi.useGetPayOrdersQuery({
    paging,
    filter: getPayOrdersSearchParams.toString()
  });

  const [addItem] = payOrdersApi.useCreatePayOrderMutation();
  const [addTransfer] = payOrdersApi.useCreateTransferPayOrderMutation();
  const [updateItem] = payOrdersApi.useUpdatePayOrderMutation();
  const [removeItem] = payOrdersApi.useDeletePayOrderMutation();

  const { dialogProps, openToAdd, openToEdit, itemToEdit } = useCreateOrEditDialog<PayOrderDto, CreatePayOrderDto, UpdatePayOrderDto, FormData>({
    addHandler: async (dto) => {
      try {
        await addItem(dto).unwrap();
        showSuccess('Запись успешно добавлена');
        closePrepaymentDialog();
        await refetchPayOrders();
      } catch (error) {
        showError('Ошибка добавления записи');
        throw extractNestJsErrorMessage(error);
      }
    },
    updateHandler: async (dto) => {
      try {
        await updateItem(dto).unwrap();
        showSuccess('Запись успешно обновлена');
        closePrepaymentDialog();
        await refetchPayOrders();
      } catch (error) {
        showError('Ошибка обновления записи');
        throw extractNestJsErrorMessage(error);
      }
    },
    formTools,
    notificationsConfig
  });

  const deletePrepayment = useDeletePrepayment();

  const handleAdd = () => {
    prepaymentToEdit.current = null;
    openPrepaymentDialog();
  };

  const onRowSelect = (payOrderId : number, isSelected : boolean) => {
    if (isSelected) {
      setSelectedPayOrderIds(prev => Array.from(new Set([...prev, payOrderId])));
    } else {
      setSelectedPayOrderIds(prev => prev.filter(item => item !== payOrderId));
    }
  };

  const handleOpenTransferDialog = () => {
    transferInitialValues.current = transferFormTools.emptyValues();
    setIsTransferDialogOpen(true);
  };

  const handleTransferSubmit = async (values: TransferFormData) => {
    try {
      await addTransfer(transferFormTools.toCreateDto(values)).unwrap();
      showSuccess('Переброска успешно внесена');
      setIsTransferDialogOpen(false);
      await refetchPayOrders();
    } catch (error) {
      showError('Ошибка внесения переброски');
      throw new Error(extractNestJsErrorMessage(error));
    }
  };

  const handleDelete = async (payOrder: PayOrderDto) => {
    try {
      await showConfirm({
        content: 'Удалить платежное поручение?'
      });

      if (payOrder.rows && payOrder.rows[0]?.salaryEntryId) {
        await deletePrepayment(payOrder.rows[0].salaryEntryId);
        showSuccessDelete();
        await refetchPayOrders();
      } else {
        await removeItem(payOrder.id).unwrap();
        showSuccessDelete();
        await refetchPayOrders();
      }
    } catch (e) {
      const error = e instanceof Error ? e : new Error('Неизвестная ошибка');
      showFailedDelete(error);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPayOrderIds.length === 0) return;

    const { payordersIds, salaryEntryIds } = splitPayordersPrepayments(collection.collection.items, selectedPayOrderIds);
    const pPromises: unknown[] = [];
    const sPromises: unknown[] = [];
    
    try {

      await showConfirm({
        content: `Удалить платежные поручения (${selectedPayOrderIds.length} шт.) ?`
      });

      payordersIds.forEach((p) => {
        pPromises.push(removeItem(p).unwrap());
      });
      salaryEntryIds.forEach((p) => {
        sPromises.push(deletePrepayment(p));
      });

      const result = await Promise.allSettled([...pPromises, ...sPromises]);
      const successedCount = result.filter((x) => x.status === 'fulfilled').length;

      if (successedCount === 0) throw new Error('Ошибка удаления ПП');

      showSuccess(`Удалено ПП ${successedCount}/${result.length}`);
      await refetchPayOrders();

      setSelectedPayOrderIds([]);

    } catch (e: unknown) {
      const error = e instanceof Error ? e : new Error('Неизвестная ошибка');
      showFailedDelete(error);
    }

  };

  const handleOpenForEdit = async (payOrder: PayOrderDto) => {
    if (payOrder.rows && payOrder.rows[0]?.salaryEntryId) {
      fetchPrepayment(payOrder.rows[0]?.salaryEntryId).then((p) => {
        prepaymentToEdit.current = p;
        openPrepaymentDialog();
      });
      return;
    }

    fetchPayOrder(payOrder.id).then((item) => {
      openToEdit(item);
    });
  };

  const handlePrepaymentSuccess = async () => {
    await refetchPayOrders();
  };

  return (
    <>
      <Heading
        title="Бухгалтерия"
        actions={
          <Stack spacing={1} direction="row">
            <Button variant="contained" startIcon={<Delete />} onClick={handleBulkDelete} size="small" disabled={selectedPayOrderIds.length === 0}>
              Удалить
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} size="small">
              Внести аванс
            </Button>
            <Button sx={{ mr: 1 }} variant="contained" onClick={openToAdd} size={'small'}>
              Внести платежное поручение
            </Button>
            <Button variant="contained" onClick={handleOpenTransferDialog} size="small">
              Внести переброску
            </Button>
            <ButtonLink buttonProps={{ variant: 'contained', size: 'small' }} to="/accounting/import">
              Импорт
            </ButtonLink>

            <ButtonLink buttonProps={{ variant: 'contained', size: 'small' }} to="/accounting/export">
              Экспорт
            </ButtonLink>
          </Stack>
        }
      />

      <PayOrdersList 
        collection={collection} 
        paging={paging} 
        onChangePaging={setPaging} 
        onEditClick={handleOpenForEdit} 
        onDeleteClick={handleDelete} 
        selectedRows={selectedPayOrderIds}
        onRowSelect={onRowSelect}
      />

      <FormDialog<FormData>
        {...dialogProps}
        renderTitle={!itemToEdit ? 'Новое платежное поручение' : 'Платежное поручение'}
        renderForm={(props) => <PayOrderFormFields {...props} isNew={!itemToEdit} />}
      />

      <FormDialog<TransferFormData>
        open={isTransferDialogOpen}
        onClose={() => setIsTransferDialogOpen(false)}
        initialValues={transferInitialValues.current}
        validate={transferFormTools.validate}
        onSubmit={handleTransferSubmit}
        options={{ noResetAfterSubmit: true }}
        renderTitle="Внести переброску"
        renderForm={(props) => <PayOrderTransferFormFields {...props} />}
      />

      <PrepaymentDialogForm 
        prepayment={prepaymentToEdit} 
        onSuccess={handlePrepaymentSuccess}
      />
    </>
  );
};

export default PayOrdersListPage;
